/**
 * Probation.jsx — v109.9.144
 * HR sub-module: lists employees whose probation is due (default: next 30 days
 * or already elapsed). Two admin actions per row:
 *   • Confirm Employment — asks effective_date, drafts email via mailto:,
 *     "Send Email" opens the user's mail agent, then "Confirm" flips
 *     employment_status to "confirmed" on the backend.
 *   • Extend Probation — asks new_probation_ends, drafts email via mailto:,
 *     "Send Email" opens mail agent, then "Extend" updates probation_ends.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { CalendarCheck, Mail, ShieldCheck, Clock, UserCheck } from "lucide-react";

const HR_CC = ["myHR@techknowskola.com", "contact@techknowskola.com"];
const COMPANY_NAME = "Panchakosh Ventures";

function buildMailto({ to, cc, subject, body }) {
  const enc = (s) => encodeURIComponent(s || "");
  const ccStr = (cc || []).filter(Boolean).join(",");
  return `mailto:${enc(to)}?cc=${enc(ccStr)}&subject=${enc(subject)}&body=${enc(body)}`;
}

function confirmationEmailBody({ empName, effectiveDate }) {
  return (
`Dear ${empName || "Employee"},

We are pleased to inform you that, based on your performance, dedication, and successful completion of your probation period, your employment with ${COMPANY_NAME} has been confirmed.

Effective ${effectiveDate || "[Effective Date]"}, your employment status will change from Probationary Employee to Confirmed Employee.

We appreciate your commitment and contributions to the organization during your probation period. We are confident that you will continue to perform with the same level of professionalism and contribute to the growth and success of the company.

As a confirmed employee, you will now be eligible for all applicable company benefits and leave entitlements as per the organization's policies.

Congratulations on this achievement. We wish you continued success and look forward to a long and rewarding association with ${COMPANY_NAME}.

If you have any questions, please feel free to contact the HR Department.

Warm Regards,

HR Department
${COMPANY_NAME}`
  );
}

function extensionEmailBody({ empName, newEnd }) {
  return (
`Dear ${empName || "Employee"},

We appreciate your efforts and contribution during your probation period with ${COMPANY_NAME} Group.

Following a review of your performance, attendance, work quality, and overall suitability for the role, management has decided to extend your probation period till ${newEnd || "[New Date]"}.

This extension is being provided to allow you additional time to demonstrate sustained improvement and consistently meet the performance standards and expectations of your position. During this period, you are expected to focus on the following areas:
  • Improving overall job performance and productivity.
  • Meeting assigned targets and deadlines.
  • Maintaining discipline, punctuality, and attendance.
  • Enhancing communication, teamwork, and professional conduct.
  • Addressing any role-specific performance concerns communicated by your Reporting Manager.

Your performance will continue to be monitored by your Reporting Manager and the HR Department. At the end of the extended probation period, the management will review your performance and may:
  • Confirm your employment;
  • Extend the probation period further, if deemed necessary; or
  • Discontinue your employment in accordance with the terms of your Appointment Letter and the Company's policies.

Please note that this extension of probation does not constitute confirmation of employment or guarantee continued employment. All other terms and conditions of your Appointment Letter shall remain unchanged and continue to apply during the extended probation period.

We encourage you to utilize this opportunity to demonstrate your capabilities and commitment to the organization. We wish you success and look forward to your positive progress.

Warm Regards,
Human Resources Department
${COMPANY_NAME} Group`
  );
}

export default function Probation() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);   // { emp, effective_date, email_sent, busy }
  const [extendDlg, setExtendDlg] = useState(null);     // { emp, new_end, email_sent, busy }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/probation/pending?days_ahead=30");
      setRows(data.items || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openConfirm = (emp) => {
    setConfirmDlg({
      emp,
      effective_date: new Date().toISOString().slice(0, 10),
      email_sent: false,
      busy: false,
    });
  };
  const openExtend = (emp) => {
    // Default = probation_ends + 60 days
    const cur = emp.probation_ends ? new Date(emp.probation_ends) : new Date();
    cur.setDate(cur.getDate() + 60);
    setExtendDlg({
      emp,
      new_end: cur.toISOString().slice(0, 10),
      email_sent: false,
      busy: false,
    });
  };

  const confirmMailtoUrl = useMemo(() => {
    if (!confirmDlg) return "";
    const cc = [user?.email, ...HR_CC, confirmDlg.emp.reporting_manager_email].filter(Boolean);
    return buildMailto({
      to: confirmDlg.emp.personal_email,
      cc,
      subject: "Confirmation of Employment",
      body: confirmationEmailBody({ empName: confirmDlg.emp.employee_name, effectiveDate: confirmDlg.effective_date }),
    });
  }, [confirmDlg, user?.email]);

  const extendMailtoUrl = useMemo(() => {
    if (!extendDlg) return "";
    const cc = [user?.email, ...HR_CC, extendDlg.emp.reporting_manager_email].filter(Boolean);
    return buildMailto({
      to: extendDlg.emp.personal_email,
      cc,
      subject: "Extension of Probation Period",
      body: extensionEmailBody({ empName: extendDlg.emp.employee_name, newEnd: extendDlg.new_end }),
    });
  }, [extendDlg, user?.email]);

  const doConfirm = async () => {
    if (!confirmDlg) return;
    setConfirmDlg({ ...confirmDlg, busy: true });
    try {
      await api.post("/probation/confirm", {
        employee_id: confirmDlg.emp.employee_id,
        effective_date: confirmDlg.effective_date,
      });
      toast.success(`${confirmDlg.emp.employee_name} confirmed`);
      setConfirmDlg(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setConfirmDlg({ ...confirmDlg, busy: false });
    }
  };
  const doExtend = async () => {
    if (!extendDlg) return;
    setExtendDlg({ ...extendDlg, busy: true });
    try {
      await api.post("/probation/extend", {
        employee_id: extendDlg.emp.employee_id,
        new_probation_ends: extendDlg.new_end,
      });
      toast.success(`Probation extended to ${extendDlg.new_end}`);
      setExtendDlg(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setExtendDlg({ ...extendDlg, busy: false });
    }
  };

  return (
    <div className="space-y-4" data-testid="probation-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
        <h3 className="text-[15px] font-extrabold text-slate-800 dark:text-slate-100">Probation Confirmations</h3>
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100">
          {rows.length} employee(s) pending
        </Badge>
        <Button size="sm" variant="outline" onClick={load} className="ml-auto" data-testid="probation-reload">
          <CalendarCheck className="w-4 h-4 mr-1" /> Reload
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="probation-table">
        <table className="w-full text-[12.5px]">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600">
            <tr>
              <th className="text-left p-2">Employee</th>
              <th className="text-left p-2">Code</th>
              <th className="text-left p-2">DOJ</th>
              <th className="text-left p-2">Probation Ends</th>
              <th className="text-left p-2">Days Left</th>
              <th className="text-left p-2">Reporting Manager</th>
              <th className="text-center p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-400">No probation reviews pending in the next 30 days.</td></tr>}
            {!loading && rows.map((r) => {
              const overdue = (r.days_left ?? 0) < 0;
              const soon = (r.days_left ?? 0) >= 0 && (r.days_left ?? 0) <= 7;
              return (
                <tr key={r.employee_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`probation-row-${r.employee_id}`}>
                  <td className="p-2 font-semibold">
                    <div>{r.employee_name}</div>
                    <div className="text-[10.5px] text-slate-500 font-normal">{r.designation || "—"}</div>
                  </td>
                  <td className="p-2 font-mono text-[11px]">{r.employee_code || "—"}</td>
                  <td className="p-2 font-mono text-[11px]">{r.date_of_joining || "—"}</td>
                  <td className="p-2 font-mono text-[11px]">{r.probation_ends || "—"}</td>
                  <td className="p-2">
                    <Badge className={overdue ? "bg-rose-100 text-rose-800" : soon ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}>
                      {overdue ? `Overdue ${Math.abs(r.days_left)}d` : `${r.days_left}d`}
                    </Badge>
                  </td>
                  <td className="p-2 text-[11px]">{r.reporting_manager_name || "—"}</td>
                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <Button size="sm" className="h-7 text-[10.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => openConfirm(r)} data-testid={`probation-confirm-${r.employee_id}`}>
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Confirm Employment
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10.5px] text-amber-800 border-amber-300 hover:bg-amber-50 dark:text-amber-100"
                        onClick={() => openExtend(r)} data-testid={`probation-extend-${r.employee_id}`}>
                        <Clock className="w-3.5 h-3.5 mr-1" /> Extend Probation
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm Employment dialog */}
      <Dialog open={!!confirmDlg} onOpenChange={(v) => { if (!v) setConfirmDlg(null); }}>
        <DialogContent className="max-w-lg" data-testid="probation-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Employment — {confirmDlg?.emp?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Effective Date <span className="text-rose-500">*</span></Label>
              <Input type="date" value={confirmDlg?.effective_date || ""}
                onChange={(e) => setConfirmDlg({ ...confirmDlg, effective_date: e.target.value })}
                data-testid="probation-effective-date"/>
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-700 p-3 text-[11px] text-slate-700 dark:text-slate-200 space-y-1">
              <div><b>To:</b> {confirmDlg?.emp?.personal_email || "—"}</div>
              <div><b>CC:</b> {[user?.email, ...HR_CC, confirmDlg?.emp?.reporting_manager_email].filter(Boolean).join(", ")}</div>
              <div><b>Subject:</b> Confirmation of Employment</div>
              <details className="mt-1">
                <summary className="cursor-pointer text-[11px] text-sky-700 dark:text-sky-300 font-semibold">Preview email body</summary>
                <pre className="mt-1 whitespace-pre-wrap text-[10.5px] leading-snug text-slate-700 dark:text-slate-200">
{confirmationEmailBody({ empName: confirmDlg?.emp?.employee_name, effectiveDate: confirmDlg?.effective_date })}
                </pre>
              </details>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="flex-1"
                onClick={() => {
                  window.location.href = confirmMailtoUrl;
                  setConfirmDlg({ ...confirmDlg, email_sent: true });
                }}
                data-testid="probation-confirm-send-email">
                <Mail className="w-4 h-4 mr-1.5" /> {confirmDlg?.email_sent ? "Re-open Email" : "Send Confirmation Email"}
              </Button>
            </div>
            {!confirmDlg?.email_sent && (
              <div className="text-[10.5px] text-amber-700 dark:text-amber-300 italic">
                Please send the confirmation email first, then click Confirm to update the record.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDlg(null)}>Cancel</Button>
            <Button
              onClick={doConfirm}
              disabled={!confirmDlg?.email_sent || confirmDlg?.busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              data-testid="probation-confirm-submit">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> {confirmDlg?.busy ? "Confirming…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Probation dialog */}
      <Dialog open={!!extendDlg} onOpenChange={(v) => { if (!v) setExtendDlg(null); }}>
        <DialogContent className="max-w-lg" data-testid="probation-extend-dialog">
          <DialogHeader>
            <DialogTitle>Extend Probation — {extendDlg?.emp?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New Probation End Date <span className="text-rose-500">*</span></Label>
              <Input type="date" value={extendDlg?.new_end || ""}
                min={extendDlg?.emp?.probation_ends || undefined}
                onChange={(e) => setExtendDlg({ ...extendDlg, new_end: e.target.value })}
                data-testid="probation-new-end"/>
              <div className="text-[10.5px] text-slate-500 mt-1">
                Currently ends: <span className="font-mono">{extendDlg?.emp?.probation_ends || "—"}</span>
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-[11px] text-slate-700 dark:text-slate-200 space-y-1">
              <div><b>To:</b> {extendDlg?.emp?.personal_email || "—"}</div>
              <div><b>CC:</b> {[user?.email, ...HR_CC, extendDlg?.emp?.reporting_manager_email].filter(Boolean).join(", ")}</div>
              <div><b>Subject:</b> Extension of Probation Period</div>
              <details className="mt-1">
                <summary className="cursor-pointer text-[11px] text-amber-700 dark:text-amber-300 font-semibold">Preview email body</summary>
                <pre className="mt-1 whitespace-pre-wrap text-[10.5px] leading-snug text-slate-700 dark:text-slate-200">
{extensionEmailBody({ empName: extendDlg?.emp?.employee_name, newEnd: extendDlg?.new_end })}
                </pre>
              </details>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="flex-1"
                onClick={() => {
                  window.location.href = extendMailtoUrl;
                  setExtendDlg({ ...extendDlg, email_sent: true });
                }}
                data-testid="probation-extend-send-email">
                <Mail className="w-4 h-4 mr-1.5" /> {extendDlg?.email_sent ? "Re-open Email" : "Send Extension Email"}
              </Button>
            </div>
            {!extendDlg?.email_sent && (
              <div className="text-[10.5px] text-amber-700 dark:text-amber-300 italic">
                Please send the extension email first, then click Extend to update the record.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDlg(null)}>Cancel</Button>
            <Button
              onClick={doExtend}
              disabled={!extendDlg?.email_sent || extendDlg?.busy || !extendDlg?.new_end}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              data-testid="probation-extend-submit">
              <Clock className="w-4 h-4 mr-1.5" /> {extendDlg?.busy ? "Extending…" : "Extend Probation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
